import React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon, DocumentTextIcon, ClockIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { ActivityReport, GoogleDriveAttachment } from "../../types";
import { useTranslation } from "react-i18next";
import { apiService } from "../../services/apiService";
import { useAuth } from "../../contexts/AuthContext";
import toast from "../../utils/toast";

interface UserFormData {
  userId: number;
  userName: string;
  userEmail: string;
  duration: number | null;
  forms: Array<{
    formId: number;
    formName?: string;
    formTitle?: string;
    formDescription: string;
    formStructure: string | object;
    userResponse: string | object;
  }>;
}

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: (ActivityReport & { allUserForms?: UserFormData[] }) | null;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  onClose,
  task: rawTask,
}) => {
  const { t } = useTranslation(["reports", "common"]);
  const { isAdmin, isManager, getSubmitDescriptionsLabel } = useAuth();
  const submitDescriptionsLabel = getSubmitDescriptionsLabel(
    "Gönderim Açıklamaları",
  );

  const activityInstanceId: number | null = useMemo(() => {
    if (!rawTask) return null;
    const raw =
      (rawTask as any).activityId ??
      (rawTask as any).activityInstanceId ??
      (rawTask as any).id ??
      null;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [rawTask]);

  type LatestManagerComment = {
    id?: number;
    authorName?: string | null;
    comment?: string | null;
    createdAt?: string | null;
  };

  const [latestManagerComment, setLatestManagerComment] =
    useState<LatestManagerComment | null>(null);
  const [loadingManagerComment, setLoadingManagerComment] = useState(false);
  const [newManagerComment, setNewManagerComment] = useState("");
  const [savingManagerComment, setSavingManagerComment] = useState(false);
  const [previewingDriveFileId, setPreviewingDriveFileId] = useState<string | null>(
    null,
  );

  const handlePreviewAttachment = useCallback(async (attachment: GoogleDriveAttachment) => {
    if (!attachment?.id) return;

    const previewWindow = apiService.shouldOpenPreviewWindow(
      attachment.name,
      attachment.mimeType,
    )
      ? window.open("", "_blank")
      : null;
    if (previewWindow) {
      previewWindow.opener = null;
      apiService.preparePreviewWindow(previewWindow, attachment.name);
    }

    try {
      setPreviewingDriveFileId(attachment.id);
      await apiService.previewGoogleDriveFile(
        attachment.id,
        attachment.name || "preview",
        previewWindow,
      );
    } catch (error) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      toast.error("Dosya görüntülenemedi.");
    } finally {
      setPreviewingDriveFileId(null);
    }
  }, []);

  // ============= Helper Functions =============

  const getStatus = () => {
    return (task as any).activityStatus || (task as any).status || "PENDING";
  };

  const getTimeInfo = (): {
    startTime: string | null;
    endTime: string | null;
  } => {
    if (!task.userDurations) return { startTime: null, endTime: null };

    try {
      const durations = JSON.parse(task.userDurations);
      const userIds = Object.keys(durations);

      let earliestStart: string | null = null;
      let latestEnd: string | null = null;

      userIds.forEach((userId) => {
        const userData = durations[userId];
        if (userData && typeof userData === "object") {
          if (userData.startTime) {
            if (!earliestStart || userData.startTime < earliestStart) {
              earliestStart = userData.startTime;
            }
          }
          if (userData.endTime) {
            if (!latestEnd || userData.endTime > latestEnd) {
              latestEnd = userData.endTime;
            }
          }
        }
      });

      return { startTime: earliestStart, endTime: latestEnd };
    } catch (error) {
      return { startTime: null, endTime: null };
    }
  };

  const parseDateTimeString = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const loadLatestManagerComment = async () => {
    if (!activityInstanceId) return;
    try {
      setLoadingManagerComment(true);
      const response = await apiService.getLatestActivityManagerComments({
        activityInstanceIds: [activityInstanceId],
      });
      const row =
        response?.data?.activities?.[String(activityInstanceId)] ??
        response?.data?.activities?.[activityInstanceId] ??
        null;
      if (row && typeof row === "object") {
        setLatestManagerComment({
          id: row.id,
          authorName: row.authorName ?? null,
          comment: row.comment ?? null,
          createdAt: row.createdAt ?? null,
        });
      } else {
        setLatestManagerComment(null);
      }
    } catch (error) {
      setLatestManagerComment(null);
    } finally {
      setLoadingManagerComment(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadLatestManagerComment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activityInstanceId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!(isAdmin() || isManager())) return;
    if (!latestManagerComment?.comment) return;
    // Prefill once for edit UX (do not overwrite if user already typed).
    setNewManagerComment((prev) =>
      prev && prev.trim().length > 0 ? prev : String(latestManagerComment.comment)
    );
  }, [isOpen, latestManagerComment?.comment, isAdmin, isManager]);

  const handleSaveManagerComment = async () => {
    if (!activityInstanceId) return;
    const text = newManagerComment.trim();
    if (!text) return;

    try {
      setSavingManagerComment(true);
      await apiService.createActivityManagerComment({
        activityInstanceId,
        comment: text,
        isGlobal: true,
      });
      setNewManagerComment("");
      await loadLatestManagerComment();
    } catch (error) {
    } finally {
      setSavingManagerComment(false);
    }
  };

  const renderManagerCommentSection = () => {
    return (
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 bg-emerald-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("reports:managerComment")}
          </h3>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          {loadingManagerComment ? (
            <div className="text-sm text-gray-600">{t("common:loading")}</div>
          ) : latestManagerComment?.comment ? (
            <div className="space-y-2">
              <div className="text-xs text-gray-600">
                {latestManagerComment.authorName ? (
                  <span className="font-semibold text-emerald-700">
                    {latestManagerComment.authorName}
                  </span>
                ) : (
                  <span className="text-gray-500">-</span>
                )}
                {latestManagerComment.createdAt ? (
                  <span className="ml-2 text-gray-500">
                    {new Date(latestManagerComment.createdAt).toLocaleString(
                      "tr-TR"
                    )}
                  </span>
                ) : null}
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {latestManagerComment.comment}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-600">
              {t("reports:noManagerComment")}
            </div>
          )}

          {(isAdmin() || isManager()) && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-700 mb-2">
                {t("reports:managerComment")}
              </label>
              <textarea
                value={newManagerComment}
                onChange={(e) => setNewManagerComment(e.target.value)}
                placeholder={t("reports:managerCommentPlaceholder")}
                rows={3}
                className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveManagerComment}
                  disabled={savingManagerComment || !newManagerComment.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  {savingManagerComment
                    ? t("common:saving")
                    : t("reports:saveManagerComment")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const calculateBreakHours = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string
  ): number => {
    if (!breaks || breaks.length === 0) return 0;
    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;
        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;
    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();
    return totalMs / (1000 * 60 * 60);
  };

  const formatBreakLabel = (
    breaks: Array<{ start: string; end: string }>
  ): string => {
    if (!breaks || breaks.length === 0) return "";
    return breaks
      .map((entry) => {
        if (!entry?.start || !entry?.end) return null;
        const startParts = entry.start.split(" ");
        const endParts = entry.end.split(" ");
        const startDate = startParts[0] || "";
        const endDate = endParts[0] || "";
        const startTime = startParts[startParts.length - 1];
        const endTime = endParts[endParts.length - 1];
        if (startDate && endDate && startDate !== endDate) {
          return `${startDate} ${startTime} - ${endDate} ${endTime}`;
        }
        return `${startTime} - ${endTime}`;
      })
      .filter(Boolean)
      .join(", ");
  };

  const parseSubmissionData = (data?: string | object) => {
    if (!data) return {};
    if (typeof data === "object") return data;
    try {
      return JSON.parse(data);
    } catch (error) {
      return {};
    }
  };

  const parseFormStructure = (data?: string | object) => {
    if (!data) return null;
    if (typeof data === "object") return data;
    try {
      return JSON.parse(data);
    } catch (error) {
      return null;
    }
  };

  const getFieldLabel = (fieldId: string, formStructure: any): string => {
    if (!formStructure) {
      return fieldId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    }

    if (formStructure.fields && Array.isArray(formStructure.fields)) {
      const field = formStructure.fields.find((f: any) => f.id === fieldId);
      if (field?.label) return field.label;
    }

    if (formStructure.elements && Array.isArray(formStructure.elements)) {
      const element = formStructure.elements.find(
        (el: any) => el.id === fieldId
      );
      if (element?.label) return element.label;
    }

    return fieldId.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getAttachments = (
    activity: ActivityReport
  ): GoogleDriveAttachment[] => {
    try {
      return activity.attachments ? JSON.parse(activity.attachments) : [];
    } catch (error) {
      return [];
    }
  };

  // ============= Render Sections =============

  // SECTION 1: DURATIONS (PRIORITY - TOP)
  const renderDurationsSection = () => {
    if (!task.userDurations) return null;

    try {
      const durations = JSON.parse(task.userDurations);
      const userCount = Object.keys(durations).length;

      if (userCount === 0) return null;

      // Build user name map
      const userNameMap: Record<string, string> = {};

      if (task.allUserForms) {
        task.allUserForms.forEach((userData: UserFormData) => {
          userNameMap[userData.userId.toString()] = userData.userName;
        });
      }

      if (task.submitDescription) {
        try {
          const descriptions = JSON.parse(task.submitDescription);
          if (Array.isArray(descriptions)) {
            descriptions.forEach((desc: any) => {
              if (desc.userId && desc.userName) {
                userNameMap[desc.userId.toString()] = desc.userName;
              }
            });
          }
        } catch (e) {}
      }

      if (task.formSubmissions) {
        task.formSubmissions.forEach((form: any) => {
          if (form.userId && form.userName) {
            userNameMap[form.userId.toString()] = form.userName;
          }
        });
      }

      // Multiple users
      if (userCount > 1) {
        const userDurationData = Object.entries(durations).map(
          ([userId, duration]: [string, any]) => {
            const startTime =
              typeof duration === "object" ? duration.startTime : null;
            const endTime =
              typeof duration === "object" ? duration.endTime : null;
            const breaks =
              typeof duration === "object" && Array.isArray(duration.breaks)
                ? duration.breaks
                : [];
            const breakHours = calculateBreakHours(
              breaks,
              startTime || undefined,
              endTime || undefined
            );
            const breakLabel = formatBreakLabel(breaks);

            let durationValue =
              typeof duration === "object" && duration.duration
                ? duration.duration
                : typeof duration === "number"
                ? duration
                : 0;

            if (startTime && endTime) {
              const start = new Date(startTime);
              const end = new Date(endTime);
              if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                const diffMs = end.getTime() - start.getTime();
                if (diffMs > 0) {
                  const rawHours = diffMs / (1000 * 60 * 60);
                  durationValue =
                    Math.round(Math.max(rawHours - breakHours, 0) * 10) / 10;
                }
              }
            }

            return {
              userId,
              startTime,
              endTime,
              durationValue,
              breakHours,
              breakLabel,
              userName:
                userNameMap[userId] || `${t("reports:user")} ${userId}`,
            };
          }
        );

        const totalDuration = userDurationData.reduce(
          (sum, u) => sum + u.durationValue,
          0
        );

        return (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("reports:userDurations")}
            </h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-blue-800">
                  {t("reports:totalDuration")}
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-blue-700">
                    {totalDuration.toFixed(2)}
                  </span>
                  <span className="text-sm text-blue-700">
                    {t("reports:hours")}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {userDurationData.map((userData) => (
                <div
                  key={userData.userId}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 text-xs font-bold">
                        {userData.userName.charAt(0).toUpperCase()}
                      </div>
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {userData.userName}
                      </p>
                    </div>
                    <div className="flex items-baseline gap-1 text-blue-700 flex-shrink-0">
                      <span className="text-xl font-bold">
                        {userData.durationValue.toFixed(2)}
                      </span>
                      <span className="text-xs">{t("reports:hours")}</span>
                    </div>
                  </div>

                  {(userData.startTime && userData.endTime) || userData.breakHours > 0 ? (
                    <div className="space-y-1.5 text-xs text-gray-600 border-t border-gray-100 pt-3">
                      {userData.startTime && userData.endTime && (
                        <div className="flex items-center gap-1.5">
                          <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                          <span>
                            {userData.startTime} - {userData.endTime}
                          </span>
                        </div>
                      )}
                      {userData.breakHours > 0 && (
                        <div className="text-orange-700">
                          {t("reports:breakDuration")}: {userData.breakHours.toFixed(2)}{" "}
                          {t("reports:hours")}
                        </div>
                      )}
                      {userData.breakLabel && (
                        <div className="text-gray-500">
                          {t("reports:breaks")}: {userData.breakLabel}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Single user
      const singleUserDuration = Object.values(durations)[0] as any;
      const startTime =
        typeof singleUserDuration === "object"
          ? singleUserDuration.startTime
          : null;
      const endTime =
        typeof singleUserDuration === "object"
          ? singleUserDuration.endTime
          : null;
      const breaks =
        typeof singleUserDuration === "object" &&
        Array.isArray(singleUserDuration.breaks)
          ? singleUserDuration.breaks
          : [];
      const breakHours = calculateBreakHours(
        breaks,
        startTime || undefined,
        endTime || undefined
      );
      const breakLabel = formatBreakLabel(breaks);

      let durationValue =
        typeof singleUserDuration === "object" &&
        singleUserDuration.duration
          ? singleUserDuration.duration
          : typeof singleUserDuration === "number"
          ? singleUserDuration
          : 0;

      if (startTime && endTime) {
        const start = new Date(startTime);
        const end = new Date(endTime);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          const diffMs = end.getTime() - start.getTime();
          if (diffMs > 0) {
            const rawHours = diffMs / (1000 * 60 * 60);
            durationValue =
              Math.round(Math.max(rawHours - breakHours, 0) * 10) / 10;
          }
        }
      }

      return (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("reports:activityDuration")}
          </h3>

          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <p className="text-xs uppercase font-semibold text-blue-700 mb-1.5">
                  {t("reports:activityDuration")}
                </p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-blue-700">
                    {durationValue.toFixed(2)}
                  </span>
                  <span className="text-sm text-blue-700">{t("reports:hours")}</span>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:col-span-2">
                <p className="text-xs uppercase font-semibold text-gray-500 mb-1.5">
                  {t("reports:startedAt")} - {t("reports:completedAt")}
                </p>
                <p className="text-sm text-gray-800">
                  {startTime && endTime ? `${startTime} - ${endTime}` : "-"}
                </p>
                {breakHours > 0 && (
                  <div className="mt-2 text-xs text-orange-700">
                    {t("reports:breakDuration")}: {breakHours.toFixed(2)} {t("reports:hours")}
                    {breakLabel ? ` (${t("reports:breaks")}: ${breakLabel})` : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      return null;
    }
  };

  // SECTION 2: USER FORMS (SECOND PRIORITY)
  const renderUserFormsSection = () => {
    // Multi-user forms
    if (task.allUserForms && task.allUserForms.length > 0) {
      return (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("reports:allUserFormResponses")} ({task.allUserForms.length}{" "}
            {t("reports:users")})
          </h3>
          <div className="space-y-6">
            {task.allUserForms.map((userData: UserFormData) => (
              <div
                key={userData.userId}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
              >
                {/* User Header */}
                <div className="bg-gray-50 border-b border-gray-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-lg">
                      {userData.userName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">
                        {userData.userName}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {userData.userEmail}
                      </p>
                    </div>
                  </div>
                </div>

                {/* User's Forms */}
                <div className="p-6">
                  {userData.forms && userData.forms.length > 0 ? (
                    <div className="space-y-4">
                      {userData.forms.map((form) => {
                        const userResponse = parseSubmissionData(
                          form.userResponse
                        );
                        const formStructure = parseFormStructure(
                          form.formStructure
                        );
                        const hasData = Object.keys(userResponse).length > 0;

                        return (
                          <div
                            key={form.formId}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            <div className="bg-gray-50 p-4 border-b border-gray-200">
                              <h5 className="font-semibold text-gray-900">
                                {form.formName ||
                                  form.formTitle ||
                                  t("reports:form")}
                              </h5>
                              {form.formDescription && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {form.formDescription}
                                </p>
                              )}
                            </div>

                            <div className="p-4 bg-white">
                              {hasData ? (
                                <div className="space-y-3">
                                  {Object.entries(userResponse).map(
                                    ([key, value]) => (
                                      <div
                                        key={key}
                                        className="bg-gray-50 rounded px-4 py-3 border border-gray-100"
                                      >
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                                          {getFieldLabel(key, formStructure)}
                                        </p>
                                        <p className="text-sm text-gray-900">
                                          {typeof value === "object"
                                            ? JSON.stringify(value, null, 2)
                                            : String(value)}
                                        </p>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic text-center py-3">
                                  {t("reports:noResponseForForm")}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">
                      {t("reports:userNotSubmitted")}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Single user forms
    if (task.formSubmissions && task.formSubmissions.length > 0) {
      return (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("reports:submittedForms")} ({task.formSubmissions.length})
          </h3>
          <div className="space-y-4">
            {task.formSubmissions.map((form) => {
              const submissionData = parseSubmissionData(form.submissionData);
              const formStructure = parseFormStructure(form.formStructure);
              const hasData = Object.keys(submissionData).length > 0;

              return (
                <div
                  key={form.formId}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                >
                  {/* User Info Header */}
                  {form.userName && (
                    <div className="bg-gray-50 border-b border-gray-200 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-semibold">
                            {form.userName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">
                              {form.userName}
                            </p>
                            {form.userEmail && (
                              <p className="text-xs text-gray-600">
                                {form.userEmail}
                              </p>
                            )}
                          </div>
                        </div>
                        {form.submittedAt && (
                          <span className="text-xs text-gray-500">
                            {new Date(form.submittedAt).toLocaleDateString(
                              "tr-TR",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Form Content */}
                  <div className="p-5">
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900">
                        {form.formName}
                      </h4>
                      {form.formDescription && (
                        <p className="text-sm text-gray-600 mt-1">
                          {form.formDescription}
                        </p>
                      )}
                    </div>

                    {hasData ? (
                      <div className="space-y-3">
                        {Object.entries(submissionData).map(([key, value]) => (
                          <div
                            key={key}
                            className="bg-gray-50 rounded px-4 py-3 border border-gray-100"
                          >
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                              {getFieldLabel(key, formStructure)}
                            </p>
                            <p className="text-sm text-gray-900">
                              {typeof value === "object"
                                ? JSON.stringify(value, null, 2)
                                : String(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic text-center py-3">
                        {t("reports:noSubmissionData")}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  // SECTION 3: GENERAL INFO (TOP)
  const renderGeneralInfoSection = () => {
    const timeInfo = getTimeInfo();
    const status = getStatus();
    const activityDescription = (
      (task as any).description ||
      task.activityDescription ||
      ""
    ).trim();

    // Build user name map
    const userNameMap: Record<string, string> = {};

    if (task.allUserForms) {
      task.allUserForms.forEach((userData: UserFormData) => {
        userNameMap[userData.userId.toString()] = userData.userName;
      });
    }

    if (task.submitDescription) {
      try {
        const descriptions = JSON.parse(task.submitDescription);
        if (Array.isArray(descriptions)) {
          descriptions.forEach((desc: any) => {
            if (desc.userId && desc.userName) {
              userNameMap[desc.userId.toString()] = desc.userName;
            }
          });
        }
      } catch (e) {}
    }

    if (task.formSubmissions) {
      task.formSubmissions.forEach((form: any) => {
        if (form.userId && form.userName) {
          userNameMap[form.userId.toString()] = form.userName;
        }
      });
    }

    const dateToUseStart = timeInfo.startTime || task.activityCreatedAt;
    const dateToUseEnd = timeInfo.endTime || task.activityCompletedAt;

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t("reports:taskInformation")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
              {t("reports:status")}
            </p>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                status === "COMPLETED"
                  ? "bg-green-100 text-green-800 border border-green-200"
                  : status === "ACTIVE" || status === "IN_PROGRESS"
                  ? "bg-blue-100 text-blue-800 border border-blue-200"
                  : status === "PENDING"
                  ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                  : status === "PENDING_APPROVAL"
                  ? "bg-amber-100 text-amber-800 border border-amber-200"
                  : status === "REJECTED"
                  ? "bg-red-100 text-red-800 border border-red-200"
                  : "bg-red-100 text-red-800 border border-red-200"
              }`}
            >
              {status === "COMPLETED"
                ? t("reports:statusCompleted")
                : status === "ACTIVE" || status === "IN_PROGRESS"
                ? t("reports:statusInProgress")
                : status === "PENDING"
                ? t("reports:statusPending")
                : status === "PENDING_APPROVAL"
                ? t("reports:statusPendingApproval")
                : status === "REJECTED"
                ? t("reports:statusRejected")
                : t("reports:statusCancelled")}
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
              {t("reports:assignedTo")}
            </p>
            <div className="text-sm font-medium text-gray-900">
              {(() => {
                if (task.userDurations) {
                  try {
                    const durations = JSON.parse(task.userDurations);
                    const userIds = Object.keys(durations);

                    if (userIds.length === 0) {
                      return t("reports:unassigned");
                    }

                    const userNames = userIds
                      .map((id) => userNameMap[id] || `Kullanıcı ${id}`)
                      .filter(Boolean);

                    if (userNames.length === 0) {
                      return task.assigneeName || t("reports:unassigned");
                    }

                    if (userNames.length > 1) {
                      return (
                        <div className="space-y-1">
                          {userNames.map((name, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                              <span>{name}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return userNames[0];
                  } catch (e) {
                  }
                }

                return task.assigneeName || t("reports:unassigned");
              })()}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
              {t("reports:startedAt")}
            </p>
            <p className="text-sm font-medium text-gray-900">
              {dateToUseStart
                ? new Date(dateToUseStart).toLocaleDateString("tr-TR", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-"}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
              {t("reports:completedAt")}
            </p>
            <p className="text-sm font-medium text-gray-900">
              {dateToUseEnd
                ? new Date(dateToUseEnd).toLocaleDateString("tr-TR", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-"}
            </p>
          </div>
        </div>

        {activityDescription && (
          <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
              Aktivite Açıklaması
            </p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
              {activityDescription}
            </p>
          </div>
        )}
      </div>
    );
  };

  // SECTION 4: SUBMIT DESCRIPTIONS (SEPARATE FROM GENERAL INFO)
  const renderSubmitDescriptionsSection = () => {
    if (!task.submitDescription) return null;

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {submitDescriptionsLabel}
        </h3>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          {(() => {
            try {
              const descriptions = JSON.parse(task.submitDescription);
              if (Array.isArray(descriptions)) {
                return (
                  <div className="space-y-3">
                    {descriptions.map((desc: any, index: number) => (
                      <div
                        key={index}
                        className="bg-gray-50 border-l-4 border-l-blue-500 rounded p-4"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-semibold">
                              {desc.userName?.charAt(0).toUpperCase() || "?"}
                            </div>
                            <span className="font-semibold text-gray-900 text-sm">
                              {desc.userName || "Bilinmeyen Kullanıcı"}
                            </span>
                          </div>
                          {desc.timestamp && (
                            <span className="text-xs text-gray-500">
                              {new Date(desc.timestamp).toLocaleString("tr-TR")}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap ml-10">
                          {desc.description}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              }
              return (
                <div className="bg-gray-50 border-l-4 border-l-blue-500 rounded p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {task.submitDescription}
                  </p>
                </div>
              );
            } catch (e) {
              return (
                <div className="bg-gray-50 border-l-4 border-l-blue-500 rounded p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {task.submitDescription}
                  </p>
                </div>
              );
            }
          })()}
        </div>
      </div>
    );
  };

  // SECTION 4: ATTACHMENTS (BOTTOM)
  const renderAttachmentsSection = () => {
    const attachments = getAttachments(task);
    if (attachments.length === 0) return null;

    const templateAttachments: typeof attachments = [];
    const userAttachments: typeof attachments = [];

    attachments.forEach((attachment) => {
      if (attachment.addedBy) {
        userAttachments.push(attachment);
      } else {
        templateAttachments.push(attachment);
      }
    });

    const renderAttachment = (attachment: any, isTemplate: boolean) => {
      let addedByName = "";
      if (!isTemplate && attachment.addedBy) {
        const userId = attachment.addedBy;
        if (task.assigneeId === userId) addedByName = task.assigneeName || "";
        if (!addedByName && task.formSubmissions) {
          const sub = task.formSubmissions.find(
            (fs: any) => fs.userId === userId || fs.submittedBy === userId
          );
          if (sub) addedByName = sub.userName || "";
        }
        if (
          !addedByName &&
          task.allUserForms &&
          Array.isArray(task.allUserForms)
        ) {
          const uf = task.allUserForms.find((u: any) => u.userId === userId);
          if (uf) addedByName = uf.userName;
        }
        if (!addedByName) addedByName = `Kullanıcı ${userId}`;
      }

      return (
        <button
          key={attachment.id}
          type="button"
          onClick={() => void handlePreviewAttachment(attachment)}
          disabled={previewingDriveFileId === attachment.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all group"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-10 h-10 bg-gray-50 rounded flex items-center justify-center">
              {previewingDriveFileId === attachment.id ? (
                <ArrowPathIcon className="w-6 h-6 text-blue-500 animate-spin" />
              ) : attachment.iconUrl ? (
                <img src={attachment.iconUrl} alt="" className="w-6 h-6" />
              ) : (
                <DocumentTextIcon className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600">
                {attachment.name}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {attachment.mimeType?.split("/")[1]?.toUpperCase() || "FILE"}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
            <span className={isTemplate ? "text-blue-600 font-medium" : "text-gray-700 font-medium"}>
              {isTemplate ? "Şablon Dosyası" : addedByName}
            </span>
            <span>
              {new Date(attachment.addedAt).toLocaleDateString("tr-TR")}
            </span>
          </div>
        </button>
      );
    };

    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t("reports:attachments")}
        </h3>

        <div className="space-y-6">
          {/* User Attachments */}
          {userAttachments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Kullanıcı Yüklemeleri ({userAttachments.length})
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userAttachments.map((att) => renderAttachment(att, false))}
              </div>
            </div>
          )}

          {/* Template Attachments */}
          {templateAttachments.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Şablon Dosyaları ({templateAttachments.length})
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {templateAttachments.map((att) => renderAttachment(att, true))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============= Main Render =============
  if (!isOpen || !rawTask) return null;
  const task = rawTask;

  const modalContent = (
    <div className="fixed inset-0 z-[120] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/55 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative z-[121] flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
          {/* Header - Simple Blue, No Gradient */}
          <div className="flex flex-shrink-0 items-center justify-between bg-blue-600 p-5">
            <div className="flex-1 pr-4">
              <h2 className="text-xl font-bold text-white">
                {task.activityTitle}
              </h2>
              <p className="text-blue-100 text-sm mt-1">
                {task.projectName} - {task.workflowName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 hover:bg-white/20 rounded transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>

          {/* Content - Prioritized Order */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-8">
            {/* 1. GENERAL INFO (TOP - GÖREV BİLGİLERİ) */}
            {renderGeneralInfoSection()}

            {/* 1.1 MANAGER COMMENT */}
            {renderManagerCommentSection()}

            {/* 2. DURATIONS (AFTER GENERAL INFO) */}
            {renderDurationsSection()}

            {/* 3. SUBMIT DESCRIPTIONS (AFTER DURATIONS) */}
            {renderSubmitDescriptionsSection()}

            {/* 4. USER FORMS */}
            {renderUserFormsSection()}

            {/* 5. ATTACHMENTS (BOTTOM) */}
            {renderAttachmentsSection()}
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 bg-white p-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                {t("common:close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

// Re-export for compatibility
export default TaskDetailModal;

